import { useState, useRef, useEffect } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Upload as UploadIcon, ScanLine, Sparkles, X, Camera, FileImage } from "lucide-react";
import { toast } from "sonner";
import NeuralOverlay from "@/components/NeuralOverlay";

export default function Upload() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [cameraOn, setCameraOn] = useState(false);
  const inputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const navigate = useNavigate();

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraOn(false);
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      setCameraOn(true);
      // Wait a tick for video element to mount
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      }, 50);
    } catch (e) {
      toast.error("Camera unavailable: " + (e?.message || "permission denied"));
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const w = video.videoWidth || 1280;
    const h = video.videoHeight || 720;
    canvas.width = w;
    canvas.height = h;
    canvas.getContext("2d").drawImage(video, 0, 0, w, h);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const f = new File([blob], `capture-${Date.now()}.jpg`, { type: "image/jpeg" });
        stopCamera();
        onFile(f);
      },
      "image/jpeg",
      0.92,
    );
  };

  const onFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    setFile(f);
    setResult(null);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(f);
  };

  useEffect(() => () => stopCamera(), []);

  const onDrop = (e) => {
    e.preventDefault();
    onFile(e.dataTransfer.files[0]);
  };

  const scan = async () => {
    if (!file) return;
    setScanning(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/receipts/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success("Receipt captured!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const reset = () => {
    stopCamera();
    setFile(null);
    setPreview(null);
    setResult(null);
  };

  return (
    <DashboardLayout>
      <div className="fade-up">
        <div className="mb-8">
          <div className="text-emerald-400 font-mono text-xs uppercase tracking-[0.3em] mb-2">
            Magic Scan
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tighter text-white">
            Scan a receipt
          </h1>
          <p className="text-slate-400 mt-2 text-sm">
            Powered by GPT-5.2 Vision. Reads even faded thermal print in seconds.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Left: upload zone */}
          <div>
            {cameraOn ? (
              <div className="glass chroma-strip rounded-2xl overflow-hidden subpage-hover-row" data-testid="camera-panel">
                <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
                  <span className="text-xs uppercase tracking-widest font-semibold text-emerald-400 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    LIVE CAMERA
                  </span>
                  <button
                    onClick={stopCamera}
                    className="text-slate-500 hover:text-white text-xs flex items-center gap-1"
                    data-testid="camera-close-btn"
                  >
                    <X size={12} /> CLOSE
                  </button>
                </div>
                <div className="relative bg-slate-950 min-h-[400px] flex items-center justify-center">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="max-w-full max-h-[520px] rounded-lg"
                    data-testid="camera-video"
                  />
                  <canvas ref={canvasRef} className="hidden" />
                  {/* Framing overlay */}
                  <div className="absolute inset-8 pointer-events-none">
                    <div className="absolute top-0 left-0 w-10 h-10 border-t-2 border-l-2 border-emerald-400 rounded-tl-lg" />
                    <div className="absolute top-0 right-0 w-10 h-10 border-t-2 border-r-2 border-emerald-400 rounded-tr-lg" />
                    <div className="absolute bottom-0 left-0 w-10 h-10 border-b-2 border-l-2 border-emerald-400 rounded-bl-lg" />
                    <div className="absolute bottom-0 right-0 w-10 h-10 border-b-2 border-r-2 border-emerald-400 rounded-br-lg" />
                  </div>
                </div>
                <div className="p-5 border-t border-white/5">
                  <button
                    onClick={capturePhoto}
                    data-testid="camera-capture-btn"
                    className="btn-neon w-full h-12 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold flex items-center justify-center gap-2"
                  >
                    <Camera size={16} /> Capture receipt
                  </button>
                </div>
              </div>
            ) : !preview ? (
              <label
                htmlFor="file-input"
                onDragOver={(e) => e.preventDefault()}
                onDrop={onDrop}
                data-testid="upload-dropzone"
                className="glass chroma-drop-halo rounded-2xl border-2 border-dashed border-white/10 hover:border-emerald-500/50 hover:bg-emerald-500/5 transition-colors cursor-pointer flex flex-col items-center justify-center py-20 px-8 text-center"
              >
                <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mb-5">
                  <UploadIcon size={26} className="text-emerald-400" />
                </div>
                <div className="text-xl font-semibold text-white">
                  Drop a receipt image here
                </div>
                <div className="text-slate-400 text-sm mt-2">
                  JPG, PNG or WEBP · up to 10 MB
                </div>
                <div className="mt-6 flex items-center gap-6 text-xs font-mono uppercase tracking-widest text-slate-500">
                  <span className="flex items-center gap-1.5">
                    <FileImage size={12} /> or click to browse
                  </span>
                </div>
                <input
                  ref={inputRef}
                  id="file-input"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => onFile(e.target.files[0])}
                  data-testid="file-input"
                />
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); startCamera(); }}
                  data-testid="open-camera-btn"
                  className="mt-6 h-11 px-5 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 font-medium flex items-center gap-2 z-10 relative"
                >
                  <Camera size={16} /> Use camera
                </button>
              </label>
            ) : (
              <div className="glass chroma-strip rounded-2xl overflow-hidden subpage-hover-row" data-testid="scan-preview-card">
                <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
                  <span className="text-xs uppercase tracking-widest font-semibold text-slate-400">
                    Preview
                  </span>
                  <button
                    onClick={reset}
                    className="text-slate-500 hover:text-white text-xs flex items-center gap-1"
                    data-testid="upload-reset-btn"
                  >
                    <X size={12} /> CLEAR
                  </button>
                </div>
                <div className="relative bg-slate-950/60 p-5 flex items-center justify-center min-h-[400px]">
                  <img
                    src={preview}
                    alt=""
                    className="max-w-full max-h-[520px] rounded-lg shadow-lg"
                  />
                  {scanning && (
                    <div className="absolute inset-5 rounded-lg overflow-hidden pointer-events-none">
                      <NeuralOverlay />
                      <div className="scan-overlay">
                        <div className="laser" />
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-5 border-t border-white/5 flex items-center gap-3">
                  <button
                    onClick={scan}
                    disabled={scanning}
                    data-testid="scan-btn"
                    className="btn-neon flex-1 h-12 rounded-xl bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-slate-950 font-semibold flex items-center justify-center gap-2"
                  >
                    <ScanLine size={16} />
                    {scanning ? "Extracting with GPT-5.2…" : "Extract with Magic OCR"}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Right: result panel */}
          <div className="glass chroma-strip rounded-2xl p-6 min-h-[500px] subpage-hover-row" data-testid="scan-ocr-card">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-semibold text-white tracking-tight flex items-center gap-2">
                <Sparkles size={16} className="text-emerald-400" />
                Extracted Data
              </h3>
              {result && (
                <span className="text-[10px] uppercase tracking-widest text-emerald-400 font-mono">
                  Verified
                </span>
              )}
            </div>

            {!result ? (
              <div className="text-center py-20 px-6">
                <div className="text-slate-500 text-sm">
                  Upload a receipt to see structured data appear here.
                </div>
                <div className="mt-8 grid grid-cols-2 gap-4 text-left opacity-40">
                  {["Merchant", "Date", "Tax", "Total"].map((f) => (
                    <div key={f} className="p-3 rounded-lg bg-white/5">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500">
                        {f}
                      </div>
                      <div className="h-4 mt-1 rounded bg-slate-700/30" />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-5" data-testid="ocr-result">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Merchant</div>
                  <div className="text-lg font-semibold text-white">{result.merchant}</div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Date</div>
                    <div className="font-mono text-sm text-slate-200 mt-1">{result.date}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Tax</div>
                    <div className="font-mono text-sm text-slate-200 mt-1">
                      ${Number(result.tax || 0).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Total</div>
                    <div className="font-mono text-lg font-bold text-emerald-400 mt-0.5">
                      ${Number(result.total || 0).toFixed(2)}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">
                    Auto-tagged category
                  </div>
                  <span className="inline-block text-xs uppercase tracking-widest font-medium px-2.5 py-1 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/25">
                    {result.category}
                    {result.tax_deductible && " · Deductible"}
                  </span>
                </div>

                {result.line_items?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">
                      Line items ({result.line_items.length})
                    </div>
                    <div className="divide-y divide-white/5 rounded-lg bg-white/5 border border-white/5">
                      {result.line_items.map((li, i) => (
                        <div key={i} className="px-3 py-2 flex justify-between text-sm">
                          <span className="text-slate-200">{li.description}</span>
                          <span className="font-mono text-emerald-300">
                            ${Number(li.price).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="pt-5 border-t border-white/5 flex gap-3">
                  <button
                    onClick={() => navigate(`/receipts/${result.id}`)}
                    data-testid="view-receipt-btn"
                    className="btn-neon flex-1 h-11 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm"
                  >
                    Open receipt
                  </button>
                  <button
                    onClick={reset}
                    className="h-11 px-4 rounded-lg border border-white/10 hover:bg-white/5 text-white text-sm"
                  >
                    Scan another
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
